import React, { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { toast } from "react-toastify";

import CardNew from "components/molecules/Card/CardNew";
import { getNotificationSettings, updateNotificationSettings } from "api";
import errorToast from "helpers/errorToast";
import Button from "components/atoms/Button/Button";
import testIds from "constants/testIds";
import { getObjectByKeys } from "helpers/commonHelpers";
import Setting from "./components/Setting/Setting";
import CustomSettings from "./components/CustomSettings/CustomSettings";
import {
  BILLING_NOTIFICATIONS_CUSTOM_SETTING_FIELDS,
  REQUEST_NOTIFICATIONS_CUSTOM_SETTING_FIELDS,
  SHIPMENT_NOTIFICATIONS_CUSTOM_SETTING_FIELDS,
} from "./customSettingFields";

function Notifications() {
  const intl = useIntl();
  const methods = useForm();
  const { handleSubmit, setValue } = methods;

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  useEffect(() => getSettings(), []);

  /**
   * Get the user's notification settings from API
   */
  const getSettings = () => {
    setIsLoading(true);
    getNotificationSettings()
      .then(({ data }) => setSettings(data))
      .catch((err) => errorToast(err))
      .finally(() => setIsLoading(false));
  };

  /**
   * Set the forms values
   * @param data - Notification settings
   */
  const setSettings = (data: any) => {
    // Set the values of the dropdowns; Rate Management, Request Notifications, Shipment Notifications, Billing Notifications
    setValue("settings", data.settings);
    const customRequestNotificationHashMap: any = getObjectByKeys(data.custom?.request || [], "key");
    const customShipmentNotificationHashMap: any = getObjectByKeys(data.custom?.shipment || [], "key");
    const customBillingNotificationHashMap: any = getObjectByKeys(data.custom?.billing || [], "key");

    // Preprocess the custom settings data to convert "team" and "off" values to boolean true and false
    const processedCustomSettings = {
      request:
        REQUEST_NOTIFICATIONS_CUSTOM_SETTING_FIELDS(intl).map((item: any) => ({
          key: item.key,
          value: customRequestNotificationHashMap?.[item.key]?.value === "team",
        })) || [],
      shipment:
        SHIPMENT_NOTIFICATIONS_CUSTOM_SETTING_FIELDS(intl).map((item: any) => ({
          key: item.key,
          value: customShipmentNotificationHashMap?.[item.key]?.value === "team",
        })) || [],
      billing:
        BILLING_NOTIFICATIONS_CUSTOM_SETTING_FIELDS(intl).map((item: any) => ({
          key: item.key,
          value: customBillingNotificationHashMap?.[item.key]?.value === "team",
        })) || [],
    };

    // Set the values of the custom settings if they exist
    if (data.custom?.request?.length) setValue("custom.request", processedCustomSettings.request);
    if (data.custom?.shipment?.length) setValue("custom.shipment", processedCustomSettings.shipment);
    if (data.custom?.billing?.length) setValue("custom.billing", processedCustomSettings.billing);
  };

  /**
   * On save, send notification settings to API
   * @param data - Notification settings
   */
  const onSave = (data: any) => {
    setIsSaving(true);

    const payload = {
      settings: data.settings,
      custom: data.custom
        ? {
            request:
              data.custom.request && data.settings.request_notifications === "custom"
                ? data.custom.request
                    .map((item: any) => ({ key: item.key, value: item.value ? "team" : "off" }))
                    .filter((item: any) => item !== undefined)
                : [],
            shipment:
              data.custom.shipment && data.settings.shipment_notifications === "custom"
                ? data.custom.shipment
                    .map((item: any) => ({ key: item.key, value: item.value ? "team" : "off" }))
                    .filter((item: any) => item !== undefined)
                : [],
            billing:
              data.custom.billing && data.settings.billing_notifications === "custom"
                ? data.custom.billing
                    .map((item: any) => ({ key: item.key, value: item.value ? "team" : "off" }))
                    .filter((item: any) => item !== undefined)
                : [],
          }
        : {},
    };

    // send changes to API
    updateNotificationSettings(payload)
      .then(({ data }) => {
        setSettings(data);
        toast(
          intl.formatMessage({
            id: "settings.notifications.notificationsUpdateSuccessMessage",
            defaultMessage: "Notifications setting updated sucessfully",
          }),
          { type: "success" },
        );
      })
      .catch((err) =>
        errorToast(
          err,
          intl.formatMessage({
            id: "settings.your-details.couldNotSaveSettings",
            defaultMessage: "Could not save settings. Please try again later",
          }),
        ),
      )
      .finally(() => setIsSaving(false));
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSave)}>
        <CardNew isLoading={isLoading} className="notifications-container">
          <Setting titleId="settings.notifications.requestNotifications" name="request_notifications">
            <CustomSettings fields={REQUEST_NOTIFICATIONS_CUSTOM_SETTING_FIELDS(intl)} fieldParentKey="request" />
          </Setting>
          <Setting titleId="settings.notifications.shipmentNotifications" name="shipment_notifications">
            <CustomSettings fields={SHIPMENT_NOTIFICATIONS_CUSTOM_SETTING_FIELDS(intl)} fieldParentKey="shipment" />
          </Setting>
          <Setting titleId="settings.notifications.billingNotifications" name="billing_notifications">
            <CustomSettings fields={BILLING_NOTIFICATIONS_CUSTOM_SETTING_FIELDS(intl)} fieldParentKey="billing" />
          </Setting>
          <div>
            <Button
              type="submit"
              variant="success"
              disabled={isSaving}
              data-testid={testIds.NOTIFICATIONS_SAVE_BUTTON}
              label={
                <FormattedMessage
                  id={isSaving ? "general.saving" : "general.save"}
                  defaultMessage={isSaving ? "Saving" : "Save"}
                />
              }
            />
          </div>
        </CardNew>
      </form>
    </FormProvider>
  );
}

export default Notifications;
